import { queryOptions, useQueryClient, useSuspenseQuery, type QueryClient } from '@tanstack/react-query';
import { hydraUiApi } from '@/services/api';
import type { QueryConfig } from '../shared/react-query-types';
import { requireWorkspaceId } from '../shared/workspace';
import {
  integrationKeys,
  parseIntegration,
  parsePipelinesConnection,
  pipelinesConnectionKeys,
  type Integration,
  type PipelinesConnection,
} from './entity';

/**
 * Pipelines-connection reads. Read-only (connections are created/edited from
 * the Pipelines app, not here). `staleTime: 0`: the list is mutated by the
 * connector config pages, so a revisit must not serve a stale set.
 *
 * The list endpoint returns a single `{ connections: [...] }` object — no
 * pagination or server-side filtering.
 */

const STALE_TIME = 0;

/** @public */
export async function getPipelinesConnections(
  workspaceId: number,
  qc: QueryClient,
): Promise<readonly PipelinesConnection[]> {
  const data = await hydraUiApi.integrations.getPipelines({
    params: { path: { environment_id: String(workspaceId) } },
  });
  const parsed = (data.connections ?? []).map(parsePipelinesConnection);
  // Sideload each connection into its detail slot for cheap by-id resolution.
  for (const c of parsed) {
    qc.setQueryData(pipelinesConnectionKeys.detail(workspaceId, c.id), c);
  }
  return parsed;
}

/** @public */
export const getPipelinesConnectionsQueryOptions = (workspaceId: number, qc: QueryClient) =>
  queryOptions({
    queryKey: pipelinesConnectionKeys.list(workspaceId, undefined),
    queryFn: () => getPipelinesConnections(workspaceId, qc),
    staleTime: STALE_TIME,
  });

interface UsePipelinesConnectionsOptions {
  queryConfig?: QueryConfig<typeof getPipelinesConnectionsQueryOptions>;
}

/** @public */
export const usePipelinesConnections = ({ queryConfig }: UsePipelinesConnectionsOptions = {}) => {
  const workspaceId = requireWorkspaceId();
  const qc = useQueryClient();
  return useSuspenseQuery({
    ...getPipelinesConnectionsQueryOptions(workspaceId, qc),
    ...queryConfig,
  });
};

/* ------------------------------------------------------------------------- */
/* Integration catalog ("spreadsheet") list                                  */
/* ------------------------------------------------------------------------- */

/**
 * The consolidated integration catalog (`GET /environments/:id/integrations`).
 *
 * `staleTime: 0`: the catalog is quasi-static but the list feeds live
 * enable/disable status, so a revisit must not serve a stale set.
 *
 * @public
 */
export async function getIntegrations(workspaceId: number, qc: QueryClient): Promise<Integration[]> {
  const data = await hydraUiApi.integrations.list({
    params: { path: { environment_id: String(workspaceId) } },
  });
  const parsed = data.integrations.map(parseIntegration);
  for (const i of parsed) {
    qc.setQueryData(integrationKeys.detail(workspaceId, i.id), i);
  }
  return parsed;
}

/** @public */
export const getIntegrationsQueryOptions = (workspaceId: number, qc: QueryClient) =>
  queryOptions({
    queryKey: integrationKeys.list(workspaceId, undefined),
    queryFn: () => getIntegrations(workspaceId, qc),
    staleTime: STALE_TIME,
  });

interface UseIntegrationsOptions {
  queryConfig?: QueryConfig<typeof getIntegrationsQueryOptions>;
}

/** @public */
export const useIntegrations = ({ queryConfig }: UseIntegrationsOptions = {}) => {
  const workspaceId = requireWorkspaceId();
  const qc = useQueryClient();
  return useSuspenseQuery({
    ...getIntegrationsQueryOptions(workspaceId, qc),
    ...queryConfig,
  });
};
